import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import ChartManager from "../../../managers/ChartManager";
import { getPitchZone, withDerivedPitchZone } from "../../../utils/zoneUtils";
import {
  doesPitchIncrementStrikeCount,
  isHitByPitchResult,
  isInPlayResult,
  isStrikeoutResult,
  isWalkResult,
  normalizePitchResult,
} from "../../../utils/pitchResults";

const getInitialPitchState = (isBullpen) => ({
  velocity: null,
  type: "",
  result: "",
  location: null,
  note: "",
  ...(isBullpen && { intendedZone: null }),
  zone: null,
});

const getInitialHitState = () => ({
  type: "",
  location: null,
  result: "",
  exitVelocity: "",
});

export const useChartingSession = ({ chart, onSave }) => {
  const isBullpen = chart.chartType === "bullpen";
  const zoneType = chart.zoneType;
  const disableAutoOuts = chart.disableAutoOuts || false;

  const [isPlayerModalOpen, setIsPlayerModalOpen] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [pitcher, setPitcher] = useState(chart.pitcher || null);
  const [batter, setBatter] = useState(chart.batter || null);
  const [catcher, setCatcher] = useState(chart.catcher || null);
  const [umpire, setUmpire] = useState(chart.umpire || null);
  const [pitches, setPitches] = useState(chart.pitches || []);
  const [isLoading, setIsLoading] = useState(true);
  const [isStrikeZone, setIsStrikeZone] = useState(true);
  const [currentPitch, setCurrentPitch] = useState(getInitialPitchState(isBullpen));
  const [currentHit, setCurrentHit] = useState(getInitialHitState());
  const [shouldResetPlot, setShouldResetPlot] = useState(false);
  const [shouldOpenCatcherModal, setShouldOpenCatcherModal] = useState(false);
  const [shouldOpenBatterModal, setShouldOpenBatterModal] = useState(false);
  const [shouldOpenPitcherModal, setShouldOpenPitcherModal] = useState(false);
  const [outs, setOuts] = useState(isBullpen ? null : 0);
  const [balls, setBalls] = useState(isBullpen ? null : 0);
  const [strikes, setStrikes] = useState(isBullpen ? null : 0);
  const [inning, setInning] = useState(isBullpen ? null : 1);
  const [topBottom, setTopBottom] = useState(isBullpen ? null : "Top");
  const [nextModalType, setNextModalType] = useState(null);
  const [isPitcherView, setIsPitcherView] = useState(false);

  const openPlayerModal = (type) => {
    setEditingPlayer(type);
    setIsPlayerModalOpen(true);
  };

  const getPitchesWithZones = useCallback(
    (updatedChart) =>
      (updatedChart.pitches || []).map((pitch) =>
        withDerivedPitchZone(pitch, updatedChart.zoneType || zoneType)
      ),
    [zoneType]
  );

  const refreshPitches = useCallback(async () => {
    const updatedChart = await ChartManager.getChartById(chart.id);
    const pitchesWithZones = getPitchesWithZones(updatedChart);
    setPitches(pitchesWithZones);
    onSave(pitchesWithZones);
    return updatedChart;
  }, [chart.id, getPitchesWithZones, onSave]);

  useEffect(() => {
    if (shouldOpenCatcherModal) {
      openPlayerModal("catcher");
      setShouldOpenCatcherModal(false);
    }
  }, [shouldOpenCatcherModal]);

  useEffect(() => {
    if (shouldOpenBatterModal) {
      openPlayerModal("batter");
      setShouldOpenBatterModal(false);
    }
  }, [shouldOpenBatterModal]);

  useEffect(() => {
    if (shouldOpenPitcherModal) {
      openPlayerModal("pitcher");
      setShouldOpenPitcherModal(false);
    }
  }, [shouldOpenPitcherModal]);

  useEffect(() => {
    const loadChartData = async () => {
      try {
        setIsLoading(true);
        const updatedChart = await ChartManager.getChartById(chart.id);
        const pitchesWithZones = getPitchesWithZones(updatedChart);
        setPitches(pitchesWithZones);
        setPitcher(updatedChart.pitcher || null);
        setBatter(updatedChart.batter || null);
        setCatcher(updatedChart.catcher || null);
        setUmpire(updatedChart.umpire || null);

        if (!isBullpen) {
          const lastPitch = updatedChart.pitches?.[updatedChart.pitches.length - 1];
          if (lastPitch) {
            setBalls(lastPitch.balls || 0);
            setStrikes(lastPitch.strikes || 0);
            setOuts(lastPitch.outs || 0);
            setInning(lastPitch.inning || 1);
            setTopBottom(lastPitch.topBottom || "Top");
          } else {
            setBalls(0);
            setStrikes(0);
            setOuts(0);
            setInning(1);
            setTopBottom("Top");
          }

          if (!updatedChart.pitcher) {
            setNextModalType("batter");
            setShouldOpenPitcherModal(true);
          }
        } else if (!updatedChart.pitcher) {
          setShouldOpenPitcherModal(true);
        }
      } catch (error) {
        toast.error("Failed to load chart data");
      } finally {
        setIsLoading(false);
      }
    };

    loadChartData();
  }, [chart.id, isBullpen, getPitchesWithZones]);

  const handlePlayerEdit = (type) => {
    if (type === "pitcher" && !isBullpen) {
      setNextModalType("batter");
    }
    openPlayerModal(type);
  };

  const handlePlayerSubmit = async (playerData) => {
    const loadingToast = toast.loading("Updating player...");
    try {
      let updatedData = {};
      let playerUpdate = null;

      switch (editingPlayer) {
        default:
          break;
        case "pitcher":
          playerUpdate = {
            name: playerData.name,
            pitchHand: playerData.pitchHand,
          };
          setPitcher(playerUpdate);
          updatedData.pitcher = playerUpdate;
          break;
        case "batter":
          if (!isBullpen) {
            playerUpdate = {
              name: playerData.name,
              batHand: playerData.batHand,
            };
            setBatter(playerUpdate);
            updatedData.batter = playerUpdate;
          }
          break;
        case "catcher":
          playerUpdate = {
            name: playerData.name,
          };
          setCatcher(playerUpdate);
          updatedData.catcher = playerUpdate;
          break;
        case "umpire":
          playerUpdate = {
            name: playerData.name,
          };
          setUmpire(playerUpdate);
          updatedData.umpire = playerUpdate;
          break;
      }

      const updatedChart = await ChartManager.updateChart(chart.id, updatedData);
      setIsPlayerModalOpen(false);
      onSave(updatedChart.pitches, updatedChart);

      if (nextModalType) {
        setTimeout(() => {
          openPlayerModal(nextModalType);
          setNextModalType(null);
        }, 100);
      }

      if (editingPlayer === "pitcher") {
        setCatcher(null);
      }

      toast.success("Player updated successfully", { id: loadingToast });
    } catch (error) {
      toast.error("Failed to update player", { id: loadingToast });
    }
  };

  const handlePlotPitch = (location) => {
    setShouldResetPlot(false);
    if (isStrikeZone) {
      const calculatedZone = location ? getPitchZone({ location }, zoneType) : null;
      setCurrentPitch((prev) => ({
        ...prev,
        location,
        zone: calculatedZone,
      }));
      return;
    }

    setCurrentHit((prev) => ({
      ...prev,
      location,
    }));
  };

  const handleAddPitch = async () => {
    try {
      if (!pitcher) {
        toast.error("Select a pitcher first");
        return;
      }
      if (!isBullpen && !batter) {
        toast.error("Select a batter first");
        return;
      }
      if (!currentPitch?.type) {
        toast.error("Select a pitch type");
        return;
      }
      if (!isBullpen && !currentPitch?.result) {
        toast.error("Select a pitch result");
        return;
      }
      if (currentPitch?.location?.x == null || currentPitch?.location?.y == null) {
        toast.error("Plot a pitch location first");
        return;
      }

      const isInPlay = isInPlayResult(currentPitch.result);
      if (
        isInPlay &&
        (currentHit?.location?.x == null || currentHit?.location?.y == null)
      ) {
        toast.error("Plot a spray chart location for balls in play");
        return;
      }
      const isNewBatterEvent =
        isWalkResult(currentPitch.result) ||
        isStrikeoutResult(currentPitch.result) ||
        isHitByPitchResult(currentPitch.result);

      let newBalls = balls;
      let newStrikes = strikes;
      let newOuts = outs;

      if (!isBullpen) {
        const result = normalizePitchResult(currentPitch.result);
        const hitResult = currentHit.result?.toLowerCase();
        const hitType = currentHit.type?.toLowerCase();
        const isOutType = ["groundout", "flyout", "lineout", "popout"].includes(
          hitType
        );

        if (hitResult === "double_play") {
          newOuts = Math.min(outs + 2, 3);
        } else if (hitResult === "triple_play") {
          newOuts = 3;
        } else if (isOutType || isStrikeoutResult(result)) {
          newOuts = Math.min(outs + 1, 3);
        }

        if (result === "ball") {
          newBalls = balls + 1;
        } else if (doesPitchIncrementStrikeCount(result, strikes)) {
          newStrikes = strikes + 1;
        }

        const isHit = [
          "single",
          "double",
          "triple",
          "home_run",
          "fielders_choice",
          "error",
        ].includes(hitResult);
        if (
          isWalkResult(result) ||
          isHitByPitchResult(result) ||
          isOutType ||
          isHit
        ) {
          newBalls = 0;
          newStrikes = 0;
        }

        if (newOuts >= 3 && !disableAutoOuts) {
          newOuts = 0;
          newBalls = 0;
          newStrikes = 0;

          if (topBottom === "Top") {
            setTopBottom("Bottom");
          } else {
            setTopBottom("Top");
            setInning((prev) => prev + 1);
          }

          setBatter(null);
          setPitcher(null);
          setNextModalType("batter");
          openPlayerModal("pitcher");
        }

        setBalls(newBalls);
        setStrikes(newStrikes);
        setOuts(newOuts);
      }

      const newPitch = withDerivedPitchZone(
        {
          id: Date.now().toString(),
          time: new Date().toISOString(),
          pitcher: {
            name: pitcher.name,
            pitchHand: pitcher.pitchHand,
          },
          batter: isBullpen
            ? null
            : {
                name: batter.name,
                batHand: batter.batHand,
              },
          catcher: catcher?.name || null,
          umpire: umpire?.name || null,
          ...currentPitch,
          x: currentPitch.location.x,
          y: currentPitch.location.y,
        },
        zoneType
      );

      if (!isBullpen) {
        newPitch.balls = newBalls;
        newPitch.strikes = newStrikes;
        newPitch.outs = newOuts;
        newPitch.inning = inning;
        newPitch.topBottom = topBottom;
      }

      if (isInPlay) {
        newPitch.hitDetails = {
          x: currentHit.location.x,
          y: currentHit.location.y,
          type: currentHit.type,
          result: currentHit.result,
          exitVelocity: currentHit.exitVelocity,
        };
      }

      const shouldChangeBatter =
        (!disableAutoOuts || newOuts < 3) &&
        (isNewBatterEvent || (isInPlay && (currentHit.result || currentHit.type)));

      if (shouldChangeBatter) {
        setBatter(null);
        setShouldOpenBatterModal(true);
      }

      await ChartManager.addPitch(chart.id, newPitch);
      await refreshPitches();
      setCurrentPitch(getInitialPitchState(isBullpen));
      setCurrentHit(getInitialHitState());
      setShouldResetPlot(true);
      setTimeout(() => setShouldResetPlot(false), 200);
    } catch (error) {
      console.error("Failed to add pitch", error);
      toast.error(error?.message || "Failed to add pitch");
    }
  };

  const handleUpdatePitch = async (pitchId, updates) => {
    const loadingToast = toast.loading("Updating pitch...");
    try {
      await ChartManager.updatePitch(chart.id, pitchId, updates);
      await refreshPitches();
      toast.success("Pitch updated successfully", { id: loadingToast });
    } catch (error) {
      toast.error("Failed to update pitch", { id: loadingToast });
    }
  };

  const handleResetPitch = () => {
    setShouldResetPlot(true);
    if (isStrikeZone) {
      setCurrentPitch(getInitialPitchState(isBullpen));
    } else {
      setCurrentHit(getInitialHitState());
    }
    setTimeout(() => setShouldResetPlot(false), 200);
  };

  const handleDeletePitch = async (id) => {
    const loadingToast = toast.loading("Deleting pitch...");
    try {
      await ChartManager.deletePitch(chart.id, id);
      await refreshPitches();
      toast.success("Pitch deleted successfully", { id: loadingToast });
    } catch (error) {
      toast.error("Failed to delete pitch", { id: loadingToast });
    }
  };

  return {
    isBullpen,
    zoneType,
    isLoading,
    isPlayerModalOpen,
    editingPlayer,
    pitcher,
    batter,
    catcher,
    umpire,
    pitches,
    currentPitch,
    currentHit,
    shouldResetPlot,
    isPitcherView,
    isStrikeZone,
    setIsPlayerModalOpen,
    setCurrentPitch,
    setCurrentHit,
    setIsPitcherView,
    setIsStrikeZone,
    handlePlayerEdit,
    handlePlayerSubmit,
    handlePlotPitch,
    handleAddPitch,
    handleResetPitch,
    handleDeletePitch,
    handleUpdatePitch,
  };
};
